import { useEffect, useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { LayoutTemplate, MoreHorizontal, Pencil, Search, Trash2 } from 'lucide-react';
import { Menu, Modal, Spinner, toast } from '@vatman/ui';
import { ApiError } from '../lib/api';
import { errorText } from '../i18n';
import { ConfirmDialog } from '../pages/dashboard/parts';
import { CATEGORIES, filterTemplates, templatesApi, templatesKey, useCustomTemplates, type CustomTemplate, type TemplateCategory } from './api';
import './templates.css';

export function CategoryLabel({ category }: { category: TemplateCategory }) {
  const { t } = useTranslation();
  return <>{t(`teamTemplates.categories.${category}`)}</>;
}

/** Where the template lives: its team, or the whole organization. */
function scopeLabel(tpl: CustomTemplate, personal: string): string {
  if (tpl.scope === 'org') return tpl.orgName ?? '';
  return tpl.personal ? personal : (tpl.teamName ?? '');
}

/**
 * Team and organization templates with a category filter and search. `onUse` gets the clicked
 * template; editing and deleting (for its creator and admins) happen right here.
 */
export function TeamTemplatesGallery({ onUse, compact, busyId }: { onUse: (tpl: CustomTemplate) => void; compact?: boolean; busyId?: string | null }) {
  const { t } = useTranslation();
  const list = useCustomTemplates();
  const [category, setCategory] = useState<TemplateCategory | 'all'>('all');
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState<CustomTemplate | null>(null);
  const [deleting, setDeleting] = useState<CustomTemplate | null>(null);
  const qc = useQueryClient();
  const all = list.data ?? [];
  const shown = filterTemplates(all, category, q);

  const remove = async (tpl: CustomTemplate) => {
    try {
      await templatesApi.remove(tpl.id);
      qc.setQueryData<CustomTemplate[]>(templatesKey, (prev) => prev?.filter((x) => x.id !== tpl.id));
      toast(t('teamTemplates.deleted'));
    } catch (err) {
      toast(errorText(err instanceof ApiError ? err.code : 'internal'));
    }
  };

  return (
    <div className={`tpl-gallery${compact ? ' compact' : ''}`}>
      <div className="tpl-filters">
        <div className="search-field tpl-search">
          <Search size={15} />
          <input className="input sm" value={q} onChange={(ev) => setQ(ev.target.value)} placeholder={t('teamTemplates.search')} aria-label={t('teamTemplates.search')} />
        </div>
        <div className="tpl-chips" role="tablist">
          {(['all', ...CATEGORIES] as const).map((c) => (
            <button key={c} type="button" className="chip" role="tab" aria-selected={category === c} data-active={category === c} onClick={() => setCategory(c)}>
              {c === 'all' ? t('teamTemplates.all') : <CategoryLabel category={c} />}
            </button>
          ))}
        </div>
      </div>
      {list.isLoading ? (
        <div className="empty-state-dashed tpl-empty">
          <Spinner />
        </div>
      ) : shown.length === 0 ? (
        <div className="empty-state-dashed tpl-empty">{all.length ? t('teamTemplates.noResults') : t('teamTemplates.empty')}</div>
      ) : (
        <div className="tpl-grid">
          {shown.map((tpl) => (
            <TemplateCard key={tpl.id} tpl={tpl} busy={busyId === tpl.id} onUse={() => onUse(tpl)} onEdit={() => setEditing(tpl)} onDelete={() => setDeleting(tpl)} />
          ))}
        </div>
      )}
      <EditTemplateDialog tpl={editing} onClose={() => setEditing(null)} />
      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(v) => !v && setDeleting(null)}
        title={t('teamTemplates.deleteTitle')}
        text={t('teamTemplates.deleteConfirm', { name: deleting?.name ?? '' })}
        confirmLabel={t('teamTemplates.delete')}
        onConfirm={() => deleting && void remove(deleting)}
      />
    </div>
  );
}

function TemplateCard({ tpl, busy, onUse, onEdit, onDelete }: { tpl: CustomTemplate; busy: boolean; onUse: () => void; onEdit: () => void; onDelete: () => void }) {
  const { t } = useTranslation();
  const meta = [t(`teamTemplates.categories.${tpl.category}`), scopeLabel(tpl, t('nav.personal'))].filter(Boolean).join(' · ');
  return (
    <div className="tpl-card" role="button" tabIndex={0} onClick={onUse} onKeyDown={(ev) => (ev.key === 'Enter' || ev.key === ' ') && (ev.preventDefault(), onUse())} title={tpl.description || tpl.name}>
      <div className="tpl-preview">
        {tpl.preview ? <img src={tpl.preview} alt="" loading="lazy" draggable={false} /> : <LayoutTemplate size={26} />}
        {busy && (
          <span className="tpl-busy">
            <Spinner />
          </span>
        )}
      </div>
      <div className="tpl-card-body">
        <span className="tpl-name">{tpl.name}</span>
        <span className="tpl-meta">{meta}</span>
      </div>
      {tpl.canEdit && (
        // React events from the portaled menu bubble through here, not up to the card.
        <span style={{ display: 'contents' }} onClick={(ev) => ev.stopPropagation()} onKeyDown={(ev) => ev.stopPropagation()}>
          <Menu.Root>
            <Menu.Trigger asChild>
              <button type="button" className="icon-btn sm tpl-more" aria-label={t('common.more')}>
                <MoreHorizontal size={16} />
              </button>
            </Menu.Trigger>
            <Menu.Content align="end">
              <Menu.Item icon={<Pencil size={16} />} onSelect={onEdit}>
                {t('teamTemplates.edit')}
              </Menu.Item>
              <Menu.Item icon={<Trash2 size={16} />} danger onSelect={onDelete}>
                {t('teamTemplates.delete')}
              </Menu.Item>
            </Menu.Content>
          </Menu.Root>
        </span>
      )}
    </div>
  );
}

function EditTemplateDialog({ tpl, onClose }: { tpl: CustomTemplate | null; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<TemplateCategory>('other');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!tpl) return;
    setName(tpl.name);
    setDescription(tpl.description);
    setCategory(tpl.category);
  }, [tpl]);

  const submit = async (ev: FormEvent) => {
    ev.preventDefault();
    if (!tpl || !name.trim()) return;
    setBusy(true);
    try {
      const updated = await templatesApi.update(tpl.id, { name: name.trim(), description: description.trim(), category });
      qc.setQueryData<CustomTemplate[]>(templatesKey, (prev) => prev?.map((x) => (x.id === updated.id ? updated : x)));
      onClose();
    } catch (err) {
      toast(errorText(err instanceof ApiError ? err.code : 'internal'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={!!tpl} onOpenChange={(v) => !v && onClose()} title={t('teamTemplates.editTitle')} width={480}>
      <form className="tpl-save" onSubmit={submit}>
        <label className="field">
          <span>{t('teamTemplates.name')}</span>
          <input className="input" value={name} onChange={(ev) => setName(ev.target.value)} autoFocus maxLength={120} />
        </label>
        <label className="field">
          <span>{t('teamTemplates.description')}</span>
          <textarea className="input tpl-description" value={description} onChange={(ev) => setDescription(ev.target.value)} placeholder={t('teamTemplates.descriptionPlaceholder')} maxLength={1000} rows={2} />
        </label>
        <div className="field">
          <span>{t('teamTemplates.category')}</span>
          <div className="tpl-chips" role="radiogroup">
            {CATEGORIES.map((c) => (
              <button key={c} type="button" className="chip" role="radio" aria-checked={category === c} data-active={category === c} onClick={() => setCategory(c)}>
                <CategoryLabel category={c} />
              </button>
            ))}
          </div>
        </div>
        <div className="tpl-actions">
          <button type="button" className="btn" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button className="btn primary" disabled={busy || !name.trim()}>
            {t('common.save')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
